/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Dropdown, FormControl, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {getAllLegalTinaParent, getLegalItemById} from '../core/_requests'
import lodash from 'lodash'

type LegalTINADropdownProps = {
  onSelect: (parent: any) => void
  disabled?: boolean
  originalUniqueCode?: string | null
}

type selectedParentType = {
  id: string | number
  unique_code: string
  heading: string
}

const LegalTINAParentDropdown: FC<LegalTINADropdownProps> = ({
  onSelect,
  disabled = false,
  originalUniqueCode,
}) => {
  const axiosPrivate = useAxiosPrivate()
  const [legalParents, setLegalParents] = useState<Array<any>>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedLegalParent, setSelectedLegalParent] = useState<selectedParentType>()
  const [offset, setOffset] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const limit = 50

  const fetchData = async (search: string, currentOffset: number, isAppending: boolean = false) => {
    try {
      setLoading(true)
      const res = await getAllLegalTinaParent(axiosPrivate, {
        limit,
        offset: currentOffset,
        search
      })
      
      const newParents = res.data.data
      
      if (newParents.length < limit) {
        setHasMore(false)
      } else {
        setHasMore(true)
      }

      if (isAppending) {
        setLegalParents(prev => {
          const existingIds = new Set(prev.map(p => p.id))
          const uniqueNewParents = newParents.filter((p: any) => !existingIds.has(p.id))
          return [...prev, ...uniqueNewParents]
        })
      } else {
        setLegalParents(newParents)
      }
    } catch (e) {
      console.error('Failed to fetch legal parents', e)
    } finally {
      setLoading(false)
    }
  }

  const debouncedSearch = useCallback(
    lodash.debounce((search: string) => {
      setOffset(0)
      fetchData(search, 0, false)
    }, 500),
    []
  )

  const getParentCode = (code: string) => {
    const parts = code.trim().split(/\s+/)
    let rightmostNonZeroIdx = parts.length - 1
    while (
      rightmostNonZeroIdx > 0 &&
      (parts[rightmostNonZeroIdx] === '00' || parts[rightmostNonZeroIdx] === '0')
    ) {
      rightmostNonZeroIdx--
    }

    if (rightmostNonZeroIdx === 0) return null

    const parentParts = [...parts]
    parentParts[rightmostNonZeroIdx] = '0'.repeat(parts[rightmostNonZeroIdx].length)
    return parentParts.join(' ')
  }

  useEffect(() => {
    fetchData('', 0, false)
  }, [])

  useEffect(() => {
    if (!originalUniqueCode) {
      setSelectedLegalParent(undefined)
      return
    }

    const parentCode = getParentCode(originalUniqueCode)
    if (!parentCode) {
      setSelectedLegalParent(undefined)
      return
    }

    const found = legalParents.find((e) => e.unique_code.trim() === parentCode.trim())
    if (found) {
      setSelectedLegalParent(found)
    } else if (!selectedLegalParent || selectedLegalParent.unique_code.trim() !== parentCode.trim()) {
      getLegalItemById(axiosPrivate, parentCode)
        .then((res) => {
          if (res.data.data && res.data.data.legalResult) {
            setSelectedLegalParent({
              id: res.data.data.legalResult.id,
              unique_code: res.data.data.legalResult.unique_code,
              heading: res.data.data.legalResult.heading
            })
          }
        })
        .catch((e) => {
          console.error('Failed to fetch selected legal parent by unique code', e)
        })
    }
  }, [originalUniqueCode, legalParents])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }

  const handleLoadMore = (e: React.MouseEvent) => {
    e.stopPropagation()
    const nextOffset = offset + limit
    setOffset(nextOffset)
    fetchData(searchTerm, nextOffset, true)
  }

  const handleSelect = (parent: any) => {
    onSelect(parent.id)
    setSelectedLegalParent({
      id: parent.id,
      unique_code: parent.unique_code,
      heading: parent.heading,
    })
  }

  const handleToggle = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
    if (!isOpen && searchTerm !== '') {
      setSearchTerm('')
      setOffset(0)
      fetchData('', 0, false)
    }
  }

  const dropdownItem = legalParents.map((parent) => {
    return (
      <Dropdown.Item
        key={parent.id}
        role='menuitem'
        size='sm'
        onClick={() => handleSelect(parent)}
        title={parent.heading}
        style={{whiteSpace: 'normal'}}
      >
        {parent.unique_code} - {parent.heading}
      </Dropdown.Item>
    )
  })

  return (
    <Dropdown onToggle={handleToggle} show={isDropdownOpen}>
      <Dropdown.Toggle
        variant='label-dropdown'
        id=''
        className='btn btn-bold btn-sm dropdown-toggle fw-normal text-start w-100'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '44px',
          height: 'auto',
          whiteSpace: 'normal',
        }}
        disabled={disabled}
      >
        {selectedLegalParent ? (
          <div
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {selectedLegalParent.unique_code} - {selectedLegalParent.heading}
          </div>
        ) : (
          'Select Content Parent'
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className='dropdown-econ-menu w-100' style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <div style={{ padding: '4px 8px', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
          <FormControl
            type='text'
            size='sm'
            placeholder='Search parent...'
            className='dropdown-econ-search-bar'
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        
        {loading && offset === 0 ? (
          <div className="d-flex justify-content-center p-3">
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        ) : (
          <>
            {legalParents.length === 0 && !loading && (
              <div className="text-muted text-center p-2" style={{ fontSize: '0.85rem' }}>
                No results found in database.
              </div>
            )}
            {dropdownItem}
            {hasMore && legalParents.length > 0 && (
              <div className="text-center p-2">
                <button 
                  className="btn btn-sm btn-primary w-100" 
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default LegalTINAParentDropdown
